import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import parse from "html-react-parser";
import DOMPurify from "dompurify";
import { fetchPostBySlug, addComment, deleteComment } from "../api";
import Navbar from "./navbar";
import Footer from "./sections/footer";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";

export default function BlogPostDetail() {
  const { language } = useLanguage();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentData, setCommentData] = useState({ name: "", content: "" });
  const [submittingComment, setSubmittingComment] = useState(false);
  const isAdmin = localStorage.getItem("adminToken");

  const postTitle =
    language === "fr" && post?.titleFr ? post.titleFr : post?.title;
  const postContent =
    language === "fr" && post?.contentFr ? post.contentFr : post?.content;

  const loadPost = React.useCallback(async () => {
    try {
      const data = await fetchPostBySlug(slug);
      setPost(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadPost();
    window.scrollTo(0, 0);
  }, [loadPost]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!commentData.content.trim()) return;

    setSubmittingComment(true);
    try {
      await addComment(slug, commentData);
      setCommentData({ name: "", content: "" });
      loadPost(); // Reload to show the new comment
    } catch (error) {
      console.error(error);
      alert("Failed to post comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (id) => {
    if (!window.confirm("Are you sure you want to delete this comment?"))
      return;
    try {
      await deleteComment(id);
      loadPost();
    } catch (error) {
      console.error(error);
      alert("Failed to delete comment");
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-[#131315] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#3aa1ff]"></div>
      </div>
    );

  if (!post)
    return (
      <div className="min-h-screen bg-[#131315] pt-40 px-5 text-center">
        <h1 className="text-3xl text-white font-Poppins mb-6">
          Post not found
        </h1>
        <Link
          to="/blog"
          className="text-[#3aa1ff] hover:underline font-Poppins"
        >
          Back to Blog
        </Link>
      </div>
    );

  return (
    <>
      <SEO 
        title={postTitle}
        description={postContent.replace(/<[^>]*>/g, "").slice(0, 160)}
        image={post.coverImage}
        type="article"
      />
      <Navbar />
      <div className="min-h-screen bg-white pt-10 pb-10">
        <div className="max-w-[1800px] mx-auto px-6 lg:px-20">
          {/* Minimal Back Button */}
          <Link
            to="/blog"
            className="group inline-flex items-center text-gray-400 hover:text-[#131315] mb-8 transition-all font-Inter text-xs uppercase tracking-[0.2em] font-semibold"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3 mr-2 transition-transform group-hover:-translate-x-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back
          </Link>

          {/* Split Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
            {/* Left Column: Title and Image (Sticky on Desktop) */}
            <header className="lg:col-span-5 lg:sticky lg:top-32 self-start">
              <div className="flex items-center gap-4 mb-4">
                <span className="h-[1px] w-8 bg-[#3aa1ff]"></span>
                <span className="text-[#3aa1ff] font-Poppins font-bold tracking-widest text-[10px] uppercase">
                  {new Date(post.createdAt).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              </div>

              <h1 className="text-xl md:text-4xl lg:text-2xl font-Poppins font-bold text-[#131315] leading-[1.2] mb-6">
                {postTitle}
              </h1>

              {post.coverImage && (
                <div className="w-full h-[200px] md:h-[400px] lg:h-[500px] rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src={post.coverImage}
                    alt={postTitle}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </header>

            {/* Right Column: Article Content */}
            <div className="lg:col-span-7">
              <article className="blog-content font-Inter text-[16px] lg:text-[17px] leading-[1.8] text-gray-600 text-justify">
                {parse(DOMPurify.sanitize(postContent))}
              </article>

              {/* Minimal Comment Section */}
              <section className="mt-8 pt-8 border-t border-gray-50">
                <h2 className="text-xl font-Poppins font-bold text-[#131315] mb-12 flex items-center gap-3">
                  Comments
                  <span className="text-sm font-medium text-gray-300">
                    / {post.comments?.length || 0}
                  </span>
                </h2>

                {/* Subtle Comment Form */}
                <form
                  onSubmit={handleCommentSubmit}
                  className="mb-20 flex flex-col gap-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-2">
                      <input
                        type="text"
                        value={commentData.name}
                        onChange={(e) =>
                          setCommentData({
                            ...commentData,
                            name: e.target.value,
                          })
                        }
                        placeholder="Your name (Optional)"
                        className="bg-gray-50 border-transparent border-b-gray-100 border-b rounded-none px-0 py-3 text-[#131315] text-sm focus:outline-none focus:border-b-[#3aa1ff] transition-all"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <textarea
                      value={commentData.content}
                      onChange={(e) =>
                        setCommentData({
                          ...commentData,
                          content: e.target.value,
                        })
                      }
                      placeholder="Share your thoughts..."
                      required
                      rows="3"
                      className="bg-gray-50 border-transparent border-b-gray-100 border-b rounded-none px-0 py-3 text-[#131315] text-sm focus:outline-none focus:border-b-[#3aa1ff] resize-none transition-all"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submittingComment}
                    className="bg-[#131315] text-white px-8 py-3 rounded-full text-xs font-Poppins font-bold hover:bg-[#3aa1ff] transition-all self-start disabled:opacity-50"
                  >
                    {submittingComment ? "Posting..." : "Post Comment"}
                  </button>
                </form>

                {/* Simplified Comment List */}
                <div className="flex flex-col gap-12">
                  {post.comments?.map((comment) => (
                    <div key={comment.id} className="flex gap-6 group">
                      <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 font-bold text-xs shrink-0 uppercase tracking-tighter">
                        {comment.name.charAt(0)}
                      </div>
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <h4 className="font-Poppins font-bold text-sm text-[#131315]">
                              {comment.name}
                            </h4>
                            <span className="text-gray-300 text-[10px] font-bold uppercase tracking-wider">
                              {new Date(comment.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          {isAdmin && (
                            <button
                              onClick={() => handleDeleteComment(comment.id)}
                              className="text-red-500 hover:text-red-700 text-[10px] font-bold uppercase tracking-wider transition-colors"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                        <p className="text-gray-500 font-Inter text-sm leading-relaxed max-w-xl text-justify">
                          {comment.content}
                        </p>
                      </div>
                    </div>
                  ))}
                  {(!post.comments || post.comments.length === 0) && (
                    <p className="text-gray-300 italic font-Inter text-sm">
                      No comments yet.
                    </p>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>

        <style
          dangerouslySetInnerHTML={{
            __html: `
          .blog-content h1, .blog-content h2, .blog-content h3 { font-family: 'Poppins', sans-serif; font-weight: bold; color: #131315; margin-top: 3.5rem; margin-bottom: 1.5rem; line-height: 1.3; }
          .blog-content h2 { font-size: 1.5rem; }
          .blog-content p { margin-bottom: 2rem; color: #4b5563; text-align: justify; }
          .blog-content ul, .blog-content ol { margin-left: 1.5rem; margin-bottom: 2rem; }
          .blog-content li { margin-bottom: 0.75rem; color: #4b5563; }
          .blog-content a { color: #3aa1ff; border-bottom: 1px solid #3aa1ff20; transition: all 0.3s; }
          .blog-content a:hover { border-bottom-color: #3aa1ff; }
          .blog-content blockquote { border-left: 2px solid #3aa1ff; padding-left: 2rem; font-style: italic; color: #6b7280; margin: 3rem 0; font-size: 1.1em; text-align: left; }
          .blog-content code { background: #f9fafb; padding: 0.2rem 0.4rem; border-radius: 4px; font-family: monospace; color: #131315; font-size: 0.9em; }
          .blog-content pre { background: #131315; color: #f1edfd; padding: 2rem; border-radius: 1rem; margin: 2.5rem 0; overflow-x: auto; font-size: 0.85rem; }
          .blog-content img { border-radius: 1rem; margin: 3rem auto; display: block; max-width: 100%; height: auto; }
        `,
          }}
        />
      </div>
      <Footer />
    </>
  );
}
